import type { CreditCardOffer } from '@cc-shopping/contracts/compare/schema/types';
import { BrowserStorageWrapper } from '@cc-shopping/platform-storage';
import { getCookieValue, setCookieValue } from '@cc-shopping/utils/getCookieValue';
import { isEqual } from '@cc-shopping/utils/object/isEqual';
import { type Dispatch, type SetStateAction, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as v from 'valibot';
import { MAXIMUM_COLUMNS_DESKTOP } from '../../ComparisonSection/ColumnContext';
import type { MarketplaceIds } from '../types';
import { fetchAllEntities, fetchEntitiesByIds, getIdsFromQueryParams, isValidJson, LOCAL_STORAGE_KEYS_MAP } from '../utils';
type Props = {
  idsToCompare: MarketplaceIds;
  setIdsToCompare: Dispatch<SetStateAction<MarketplaceIds>>;
  setPreviouslyViewedIds: Dispatch<SetStateAction<MarketplaceIds>>;
  DEFAULT_IDS_TO_COMPARE: MarketplaceIds;
  offersByDefault: CreditCardOffer[];
  stringArrayParser?: (values: string) => string[];
};
export function defaultStringArrayParser(json: string) {
  const parsedValue = JSON.parse(json) as string[];
  return parsedValue.filter(Boolean);
}
const dedupeOffers = (offers: CreditCardOffer[]) => {
  const addedIds = new Set<string>();
  return offers.filter(offer => {
    if (addedIds.has(offer.id)) {
      return false;
    }
    addedIds.add(offer.id);
    return true;
  });
};
export function useAllEntities({
  idsToCompare,
  setIdsToCompare,
  setPreviouslyViewedIds,
  DEFAULT_IDS_TO_COMPARE,
  offersByDefault,
  stringArrayParser = defaultStringArrayParser
}: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingFetch, setLoadingFetch] = useState(true);
  const [allEntities, setAllEntities] = useState<CreditCardOffer[]>([]);
  const [defaultOffers, setDefaultOffers] = useState<CreditCardOffer[]>(offersByDefault);
  const allOffers = allEntities.length > 0 ? allEntities : defaultOffers;
  const firstVisit = useRef(false);
  const storage = new BrowserStorageWrapper();
  const completeIdsToCompare = (array: MarketplaceIds) => {
    if (array.length < MAXIMUM_COLUMNS_DESKTOP) {
      for (let i = 0; i < MAXIMUM_COLUMNS_DESKTOP - array.length; i++) {
        array.push(null);
      }
    }
  };
  const saveLocalStoragePrevIds = (prevIdsString: string | null, compareIdsString: string | null, setter: (value: MarketplaceIds) => void, source: CreditCardOffer[]): MarketplaceIds | undefined => {
    if (prevIdsString && compareIdsString) {
      try {
        const parsedPrevIdsValue = JSON.parse(prevIdsString) as MarketplaceIds;
        const parsedCompareIdsValue = JSON.parse(compareIdsString) as MarketplaceIds;
        if (Array.isArray(parsedPrevIdsValue) && Array.isArray(parsedCompareIdsValue)) {
          const prevIds = parsedPrevIdsValue.map(id => source.find(offer => offer.id === id)?.id || null);
          const compareIds = parsedCompareIdsValue.map(id_0 => source.find(offer_0 => offer_0.id === id_0)?.id || null);
          const idsSet = new Set([...compareIds, ...prevIds]);
          const newPrevIds = Array.from(idsSet).filter(Boolean).slice(0, 10);
          setter(newPrevIds);
          return newPrevIds;
        }
      } catch (e) {
        console.error(e);
        storage.setItem(LOCAL_STORAGE_KEYS_MAP.PREVIOUS_IDS, JSON.stringify([null, null, null]));
      }
    }
  };
  const saveLocalStorageCompareIds = (value: string | null, setter_0: (value: MarketplaceIds) => void, source_0: CreditCardOffer[]): MarketplaceIds | undefined => {
    if (value) {
      try {
        const parsedValue = JSON.parse(value) as MarketplaceIds;
        if (Array.isArray(parsedValue)) {
          const compareIds_0 = parsedValue.map(id_1 => source_0.find(offer_1 => offer_1.id === id_1)?.id || null);
          completeIdsToCompare(compareIds_0);
          storage.setItem(LOCAL_STORAGE_KEYS_MAP.IDS_TO_COMPARE, JSON.stringify(compareIds_0));
          setter_0(compareIds_0);
          return compareIds_0;
        }
      } catch (e_0) {
        console.error(e_0);
        storage.setItem(LOCAL_STORAGE_KEYS_MAP.IDS_TO_COMPARE, JSON.stringify([null, null, null]));
      }
    }
  };
  const saveToolsPageOfferIds = (legacyCompareIdsRaw: string, source_1: CreditCardOffer[]): MarketplaceIds | undefined => {
    const legacyNumberArraySchema = v.array(v.number());
    if (isValidJson(legacyCompareIdsRaw)) {
      const parsedResultLegacyCompareIdsNumber = v.safeParse(legacyNumberArraySchema, JSON.parse(legacyCompareIdsRaw));
      if (parsedResultLegacyCompareIdsNumber.success) {
        const legacyCompareIds = parsedResultLegacyCompareIdsNumber.output;
        const newCompareIds: MarketplaceIds = legacyCompareIds.map(legacyId => source_1.find(offer_2 => offer_2.legacyId === legacyId)?.id || null).filter(Boolean).slice(0, MAXIMUM_COLUMNS_DESKTOP);
        setCookieValue(LOCAL_STORAGE_KEYS_MAP.TOOLS_PAGES, '[]');
        if (newCompareIds.length === 0) return;
        completeIdsToCompare(newCompareIds);
        setIdsToCompare(newCompareIds);
        storage.setItem(LOCAL_STORAGE_KEYS_MAP.IDS_TO_COMPARE, JSON.stringify(newCompareIds));
        return newCompareIds;
      }
    }
  };
  const getValuesFromUser = (): {
    compareIds: string | null;
    legacyCompareIdsRaw: string;
    prevIds: string | null;
  } => {
    const idsFromQueryParams = getIdsFromQueryParams();
    let stringifiedIdsFromQueryParams = null;
    if (idsFromQueryParams) {
      const idsFromQueryParamsArray = idsFromQueryParams?.split(',');
      stringifiedIdsFromQueryParams = JSON.stringify(idsFromQueryParamsArray);
    }
    return {
      compareIds: stringifiedIdsFromQueryParams ?? storage.getItem(LOCAL_STORAGE_KEYS_MAP.IDS_TO_COMPARE),
      legacyCompareIdsRaw: getCookieValue(LOCAL_STORAGE_KEYS_MAP.TOOLS_PAGES) ?? '',
      prevIds: storage.getItem(LOCAL_STORAGE_KEYS_MAP.PREVIOUS_IDS)
    };
  };
  const getAllEntitiesIds = ({
    prevIds: prevIds_0,
    compareIds: compareIds_1,
    legacyCompareIdsRaw: legacyCompareIdsRaw_0
  }: {
    prevIds: string | null;
    compareIds: string | null;
    legacyCompareIdsRaw: string;
  }): {
    ids: string[];
    legacyIds: string[];
  } => {
    let allIds: string[] = [];
    let legacyIds: string[] = [];
    if (prevIds_0) {
      try {
        allIds = allIds.concat(stringArrayParser(prevIds_0));
      } catch (e_1) {
        console.error(e_1);
      }
    }
    if (compareIds_1) {
      try {
        allIds = allIds.concat(stringArrayParser(compareIds_1));
      } catch (e_2) {
        console.error(e_2);
      }
    }
    if (legacyCompareIdsRaw_0) {
      try {
        legacyIds = legacyIds.concat(stringArrayParser(legacyCompareIdsRaw_0));
      } catch (e_3) {
        console.error(e_3);
      }
    }
    return {
      ids: allIds,
      legacyIds
    };
  };
  useLayoutEffect(() => {
    const promises = [fetchAllEntities().then(allEntities_0 => {
      setAllEntities(dedupeOffers([...defaultOffers, ...allEntities_0]));
    })];
    const {
      compareIds: compareIds_2,
      legacyCompareIdsRaw: legacyCompareIdsRaw_1,
      prevIds: prevIds_1
    } = getValuesFromUser();
    firstVisit.current = !compareIds_2 && !legacyCompareIdsRaw_1 && !prevIds_1;
    if (firstVisit.current) {
      setIsLoading(false);
    } else {
      promises.push(fetchEntitiesByIds(getAllEntitiesIds({
        prevIds: prevIds_1,
        compareIds: compareIds_2,
        legacyCompareIdsRaw: legacyCompareIdsRaw_1
      })).then(res => {
        if (res.length > 0) {
          setDefaultOffers(prevState => dedupeOffers([...prevState, ...res]));
        }
      }));
    }
    void (async () => {
      await Promise.race(promises);
      setLoadingFetch(false);
    })();
  }, []);
  useEffect(() => {
    if (!firstVisit.current && !loadingFetch) {
      setIsLoading(isEqual(idsToCompare, DEFAULT_IDS_TO_COMPARE) && allOffers.length === 0);
    }
  }, [idsToCompare, loadingFetch, allOffers]);
  useEffect(() => {
    if (allOffers.length > 0 && !loadingFetch) {
      const {
        compareIds: compareIds_3,
        legacyCompareIdsRaw: legacyCompareIdsRaw_2,
        prevIds: prevIds_2
      } = getValuesFromUser();
      const currentUrl = window.location.href;
      const urlObject = new URL(currentUrl);
      urlObject.searchParams.delete('compareIds');
      window.history.replaceState({}, '', urlObject.pathname + urlObject.search);
      saveLocalStoragePrevIds(prevIds_2, compareIds_3, setPreviouslyViewedIds, allOffers);
      const ids = saveToolsPageOfferIds(legacyCompareIdsRaw_2, allOffers);
      if (!ids) {
        saveLocalStorageCompareIds(compareIds_3, setIdsToCompare, allOffers);
      }
    }
  }, [allOffers, loadingFetch]);
  return {
    allOffers,
    isLoading
  };
}